import { useLazyQuery } from '@apollo/client';
import { Badge, Button, Dialog, Table } from '@radix-ui/themes';
import type { ReactNode } from 'react';
import { useCallback, use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { LEVER_AUTHORIZE_URL } from '../../behavior/queries/integration';
import { apiFetch } from '../../utils/api';
import LocaleContext from '../hooks/LocaleContext';
import teamtailorLogo from '../../style/logos/teamtailor.svg';
import LeverConfig from './Integrations/Lever/leverConfig';
import TeamtailorConfig from './Integrations/Teamtailor/TeamtailorConfig';

declare global {
  interface Window {
    onSuccess: (status: boolean) => void;
    onError: (error: object) => void;
    onOpen: () => string;
    onClose: () => void;
  }
}

let popup: WindowProxy | null = null;

type TIntegrationRecord = {
  id: string;
  integration_name: string;
  name: string;
  clientId: string;
  archived: boolean;
  active: boolean;
  api_key?: string;
  provider_key?: string;
  company_webhook_signature_key?: string;
  createdAt: string;
  updatedAt: string;
};

const loadIntegrations = async (
  signal?: AbortSignal,
): Promise<{ ok: true; data: TIntegrationRecord[] } | { ok: false }> => {
  try {
    const response = await apiFetch('/integrations', { signal });
    if (!response.ok) return { ok: false };
    const { data } = (await response.json()) as { data: TIntegrationRecord[] };
    return { ok: true, data };
  } catch {
    return { ok: false };
  }
};

const IntegrationRows = ({
  integrationName,
  integrationStatus,
  integrationImage,
  onSettingsClick,
}: {
  integrationName: string;
  integrationStatus: boolean;
  integrationImage: ReactNode;
  onSettingsClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Table.Row align="center">
      <Table.Cell>
        <div className="lever-icon-integration-table-main">
          {integrationImage}
          <span className="lever-icon-integration-table-text">{integrationName}</span>
        </div>
      </Table.Cell>
      <Table.Cell>
        <Badge color={integrationStatus ? 'grass' : 'gray'} size="3" radius="large">
          {integrationStatus ? t('settings.integration.status.active') : t('settings.integration.status.inactive')}
        </Badge>
      </Table.Cell>
      <Table.Cell align="right">
        <Button variant="outline" onClick={onSettingsClick}>
          {t('settings.integration.settingsButton')}
        </Button>
      </Table.Cell>
    </Table.Row>
  );
};

const Integrations = () => {
  const { t } = useTranslation();
  const { userSettings } = use(LocaleContext);
  const [LeverIntegrationStatus, setLeverIntegrationStatus] = useState(false);
  const [TeamtailorIntegrationStatus, setTeamtailorIntegrationStatus] = useState(false);
  const [teamtailorCompanyName, setTeamtailorCompanyName] = useState<string>('');
  const [teamtailorApiKey, setTeamtailorApiKey] = useState<string>('');
  const [teamtailorProviderKey, setTeamtailorProviderKey] = useState<string>('');
  const [teamtailorWebhookKey, setTeamtailorWebhookKey] = useState<string>('');
  const [teamtailorExists, setTeamtailorExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leverConfigModal, setLeverConfigModal] = useState(false);
  const [teamtailorConfigModal, setTeamtailorConfigModal] = useState(false);
  const [leverIntegrationDetailsModal, setLeverIntegrationDetailsModal] = useState(false);
  const [integrationListArray, setIntegrationListArray] = useState<
    { integrationName: string; integrationStatus: boolean; integrationImage: ReactNode; onSettingsClick: () => void }[]
  >([]);

  const leverConfigModalHandleClose = () => setLeverConfigModal(false);
  const leverConfigModalHandleShow = () => setLeverConfigModal(true);

  const teamtailorConfigModalHandleClose = () => setTeamtailorConfigModal(false);
  const teamtailorConfigModalHandleShow = () => setTeamtailorConfigModal(true);

  const leverIntegrationDetailsModalHandleClose = () => setLeverIntegrationDetailsModal(false);
  const leverIntegrationDetailsModalHandleShow = () => setLeverIntegrationDetailsModal(true);

  const [getLeverAuthorizeURL] = useLazyQuery(LEVER_AUTHORIZE_URL, {
    fetchPolicy: 'cache-and-network',
  });

  const tostError = (errorMessage: string) => {
    toast.error(errorMessage);
  };

  const browser = window.self;
  browser.onSuccess = (status) => {
    setLeverIntegrationStatus(status);
    if (userSettings.company) fetchData();
    setLeverConfigModal(true);
  };

  browser.onError = () => {
    tostError(t('settings.integration.toast.integrationFailed'));
  };

  browser.onOpen = () => (userSettings.company ? userSettings.company.id : '');

  browser.onClose = () => {};

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setLeverIntegrationStatus(false);
    setTeamtailorIntegrationStatus(false);
    setTeamtailorCompanyName('');
    setTeamtailorApiKey('');
    setTeamtailorProviderKey('');
    setTeamtailorWebhookKey('');
    setTeamtailorExists(false);

    const loaded = await loadIntegrations(signal);
    if (signal?.aborted) return;
    if (!loaded.ok) {
      setIntegrationListArray([]);
      toast.error(t('settings.integration.toast.loadFailed'));
      return;
    }
    const { data } = loaded;

    const newList: {
      integrationName: string;
      integrationStatus: boolean;
      integrationImage: ReactNode;
      onSettingsClick: () => void;
    }[] = [];

    if (data && data.length > 0) {
      data.forEach((element) => {
        const integrationName = element.integration_name.toUpperCase();
        const isActive = element.active;
        if (integrationName === 'LEVER') {
          setLeverIntegrationStatus(isActive);
          newList.push({
            integrationName: 'Lever',
            integrationStatus: isActive,
            integrationImage: (
              <img
                src="/integrationLogos/lever_rgb.svg"
                alt={t('settings.integration.logoAlt')}
                className="lever-icon-integration-table-image"
              />
            ),
            onSettingsClick: leverConfigModalHandleShow,
          });
        }
        if (integrationName === 'TEAMTAILOR') {
          setTeamtailorIntegrationStatus(isActive);
          setTeamtailorCompanyName(element.name);
          setTeamtailorApiKey(element.api_key || '');
          setTeamtailorProviderKey(element.provider_key || '');
          setTeamtailorWebhookKey(element.company_webhook_signature_key || '');
          setTeamtailorExists(true);
          newList.push({
            integrationName: 'Teamtailor',
            integrationStatus: isActive,
            integrationImage: (
              <img
                src={teamtailorLogo}
                alt={t('settings.integration.logoAlt')}
                className="lever-icon-integration-table-image"
                style={{ maxHeight: '40px' }}
              />
            ),
            onSettingsClick: teamtailorConfigModalHandleShow,
          });
        }
      });
    }
    setIntegrationListArray(newList);
  }, []);

  const reloadIntegrations = useCallback(() => fetchData(), [fetchData]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const leverIntegrationPopup = async () => {
    let url: string;
    if (LeverIntegrationStatus === false) {
      const keepPopupInFocus = () => {
        setLoading(true);
        if (popup && !popup.closed) {
          popup.focus();
        }
        if (popup && popup.closed) {
          browser.onClose();
          setLoading(false);
        }
      };
      const leverAuthorizeURLResponse = await getLeverAuthorizeURL({
        variables: {
          clientId: userSettings.company?.id,
        },
      });
      if (
        leverAuthorizeURLResponse.data.clientIntegrations.Lever.authorizeURL.__typename === 'LeverAuthorizeURLSuccess'
      ) {
        url = leverAuthorizeURLResponse.data.clientIntegrations.Lever.authorizeURL.URL;
      } else {
        tostError(t('settings.integration.toast.somethingWentWrong'));
        return;
      }

      const openPopup = (x: number, y: number, width: number, height: number) => {
        popup = browser.open(
          `${url}`,
          'Lever Authentication',
          `width=${width},height=${height},top=${y},left=${x},sandbox="allow-top-navigation allow-scripts"`,
        );
      };
      const openCenteredPopup = () => {
        const width = 600;
        const height = 500;
        const left = window.outerWidth / 2 - width / 2;
        const top = window.outerHeight / 2 - height / 2 + window.screenY;

        openPopup(left, top, width, height);
      };
      openCenteredPopup();

      window.addEventListener('focus', keepPopupInFocus);
      window.addEventListener('click', keepPopupInFocus); // Workaround for Firefox
    }
  };

  return (
    <div className="border border-gray-200 rounded mb-0 p-4">
      <div className="py-2 flex justify-between items-center">
        <h2 className="mb-0">{t('settings.integration.heading')}</h2>
        <Dialog.Root>
          <Dialog.Trigger>
            <Button size="3">{t('settings.integration.addIntegrationsButton')}</Button>
          </Dialog.Trigger>
          <Dialog.Content aria-describedby={undefined}>
            <Dialog.Title>{t('settings.integration.addDialog.title')}</Dialog.Title>

            <div className="flex justify-between items-center">
              <div className="lever-icon-integration-list-main">
                <img
                  src="/integrationLogos/lever_rgb.svg"
                  alt={t('settings.integration.logoAlt')}
                  className="lever-icon-integration-list-image"
                />
                <span className="lever-icon-integration-list-text">Lever</span>
              </div>
              <Dialog.Close>
                <Button
                  color="grass"
                  size="3"
                  disabled={LeverIntegrationStatus}
                  onClick={() => {
                    leverIntegrationDetailsModalHandleShow();
                  }}
                >
                  {LeverIntegrationStatus
                    ? t('settings.integration.addDialog.statusButton.enabled')
                    : t('settings.integration.addDialog.statusButton.add')}
                </Button>
              </Dialog.Close>
            </div>

            <div className="flex justify-between items-center mt-4">
              <div className="lever-icon-integration-list-main">
                <img
                  src={teamtailorLogo}
                  alt={t('settings.integration.logoAlt')}
                  className="lever-icon-integration-list-image"
                  style={{ maxHeight: '40px' }}
                />
                <span className="lever-icon-integration-list-text">Teamtailor</span>
              </div>
              <Dialog.Close>
                <Button
                  color="grass"
                  size="3"
                  disabled={TeamtailorIntegrationStatus}
                  onClick={() => {
                    teamtailorConfigModalHandleShow();
                  }}
                >
                  {TeamtailorIntegrationStatus
                    ? t('settings.integration.addDialog.statusButton.enabled')
                    : t('settings.integration.addDialog.statusButton.add')}
                </Button>
              </Dialog.Close>
            </div>
            <div className="flex justify-end mt-4">
              <Dialog.Close>
                <Button size="3">{t('settings.integration.addDialog.closeButton')}</Button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Root>
      </div>

      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>{t('settings.integration.table.name')}</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>{t('settings.integration.table.status')}</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {integrationListArray.map((element, index) => {
            return (
              <IntegrationRows
                integrationName={element.integrationName}
                integrationStatus={element.integrationStatus}
                integrationImage={element.integrationImage}
                key={index}
                onSettingsClick={element.onSettingsClick}
              />
            );
          })}
        </Table.Body>
      </Table.Root>
      {leverConfigModal && (
        <LeverConfig
          modelShow={leverConfigModal}
          modalHandleClose={leverConfigModalHandleClose}
          updateIntegrationTable={reloadIntegrations}
          setLeverIntegrationStatus={setLeverIntegrationStatus}
        />
      )}
      {teamtailorConfigModal && (
        <TeamtailorConfig
          modelShow={teamtailorConfigModal}
          modalHandleClose={teamtailorConfigModalHandleClose}
          updateIntegrationTable={reloadIntegrations}
          isConfigured={TeamtailorIntegrationStatus}
          isExisting={teamtailorExists}
          clientId={userSettings.company?.id}
          companyName={teamtailorCompanyName}
          initialApiKey={teamtailorApiKey}
          initialProviderKey={teamtailorProviderKey}
          initialWebhookKey={teamtailorWebhookKey}
        />
      )}

      <Dialog.Root open={leverIntegrationDetailsModal} onOpenChange={leverIntegrationDetailsModalHandleClose}>
        <Dialog.Content>
          <Dialog.Title>{t('settings.integration.detailsDialog.title')}</Dialog.Title>
          <Dialog.Description>
            <div className="inline-flex items-center">
              <img
                src="/integrationLogos/lever_rgb.svg"
                alt={t('settings.integration.logoAlt')}
                className="lever-icon-integration-details-image"
              />
              <span className="lever-icon-integration-details-text">Lever</span>
            </div>
            <hr className="hr" />
            <div>
              <span>
                <p>{t('settings.integration.detailsDialog.grantAccessInstruction')}</p>
              </span>
              <span>
                <p>{t('settings.integration.detailsDialog.pressGrantAccess')}</p>
              </span>
            </div>
          </Dialog.Description>

          <div className="flex w-full">
            <div className="w-1/2 px-4">
              <Button
                size="3"
                color="grass"
                className="w-full"
                loading={loading}
                onClick={() => {
                  leverIntegrationPopup();
                  leverIntegrationDetailsModalHandleClose();
                }}
              >
                {t('settings.integration.detailsDialog.grantAccessButton')}
              </Button>
            </div>
            <div className="w-1/2 px-4">
              <Button
                color="gray"
                size="3"
                variant="soft"
                className="w-full"
                onClick={() => {
                  leverIntegrationDetailsModalHandleClose();
                }}
              >
                {t('settings.integration.detailsDialog.backButton')}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
};

export default Integrations;
